import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import { nullable, object, pattern, refine, size, string, trimmed } from 'superstruct';

import { isValidEmail } from '../utils/validators';

const LENGTH_MIN = 1;
const LENGTH_MAX_NAME = 50;
const EMOJI_PATTERN =
    /[\u{1F000}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{2194}-\u{2199}]|[\u{2B05}-\u{2B07}]|[\u{2934}-\u{2935}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]|\u{FE0F}|\u{20E3}|[\u{E0020}-\u{E007F}]|[\u{1F9B0}-\u{1F9B3}]/gu;

export const conditionalPaymentValidationSchema = (
    requireAddress?: boolean,
    getTranslation?: (key: string, defaultValue?: string) => string
) =>
    object({
        firstName: refine(size(string(), LENGTH_MIN, LENGTH_MAX_NAME), 'firstName', (value) => {
            switch (true) {
                case value === '':
                case value.length < LENGTH_MIN:
                    return (
                        getTranslation?.('payment.validation.firstNameRequired', 'First name is required.') ||
                        'First name is required.'
                    );
                case EMOJI_PATTERN.test(value):
                    return (
                        getTranslation?.('payment.validation.firstNameNoEmojis', 'First name cannot contain emojis.') ||
                        'First name cannot contain emojis.'
                    );
                default:
                    return true;
            }
        }),
        firstNameConfirm: string(),
        lastName: refine(size(string(), LENGTH_MIN, LENGTH_MAX_NAME), 'lastName', (value) => {
            switch (true) {
                case value === '':
                case value.length < LENGTH_MIN:
                    return (
                        getTranslation?.('payment.validation.lastNameRequired', 'Last Name is required.') ||
                        'Last Name is required.'
                    );
                case EMOJI_PATTERN.test(value):
                    return (
                        getTranslation?.('payment.validation.lastNameNoEmojis', 'Last name cannot contain emojis.') ||
                        'Last name cannot contain emojis.'
                    );
                default:
                    return true;
            }
        }),
        email: refine(pattern(trimmed(string()), /^\S+@\S+$/i), 'Email', (value) => {
            switch (true) {
                case value === '':
                    return (
                        getTranslation?.('payment.validation.emailRequired', 'Email is required.') ||
                        'Email is required.'
                    );
                case !isValidEmail(value):
                    return (
                        getTranslation?.('payment.validation.emailValid', 'A valid Email is required.') ||
                        'A valid Email is required.'
                    );
                default:
                    return true;
            }
        }),
        emailConfirmation: refine(
            pattern(trimmed(string()), /^\S+@\S+$/i),
            'Email',
            (value, parent) => {
                switch (true) {
                    case value === '':
                    case value.length < LENGTH_MIN:
                        return (
                            getTranslation?.(
                                'payment.validation.emailConfirmationRequired',
                                'Email Confirmation is required.'
                            ) || 'Email Confirmation is required.'
                        );
                    case !isValidEmail(value):
                        return (
                            getTranslation?.('payment.validation.emailValid', 'A valid Email is required.') ||
                            'A valid Email is required.'
                        );
                    case parent.branch[0].email !== value:
                        return (
                            getTranslation?.('payment.validation.emailsDoNotMatch', 'Emails do not match') ||
                            'Emails do not match'
                        );

                    default:
                        return true;
                }
            }
        ),
        phoneNumber: refine(size(string(), LENGTH_MIN, 15), 'email', (value) => {
            switch (true) {
                case value === '':
                case value.length < LENGTH_MIN:
                    return (
                        getTranslation?.('payment.validation.phoneNumberRequired', 'Phone Number is required.') ||
                        'Phone Number is required.'
                    );
                default:
                    return true;
            }
        }),
        streetAddress: requireAddress
            ? refine(size(string(), LENGTH_MIN, 100), 'streetAddress', (value) => {
                  switch (true) {
                      case value === '':
                      case value.length < LENGTH_MIN:
                          return (
                              getTranslation?.('payment.validation.addressRequired', 'Address is required.') ||
                              'Address is required.'
                          );
                      default:
                          return true;
                  }
              })
            : nullable(string()),
        city: requireAddress
            ? refine(size(string(), LENGTH_MIN, 100), 'city', (value) => {
                  switch (true) {
                      case value === '':
                      case value.length < LENGTH_MIN:
                          return (
                              getTranslation?.('payment.validation.cityRequired', 'City is required.') ||
                              'City is required.'
                          );
                      default:
                          return true;
                  }
              })
            : nullable(string()),
        country: refine(string(), 'country', (value) => {
            if (value?.length < 2) {
                return (
                    getTranslation?.('payment.validation.countryRequired', 'Country is required.') ||
                    'Country is required.'
                );
            } else {
                return true;
            }
        }),
        province: refine(string(), 'province', (value, context) => {
            if (context.branch[0].country === 'CA' || context.branch[0].country === 'US') {
                if (typeof value !== 'string' || value.trim().length === 0) {
                    return (
                        getTranslation?.(
                            'payment.validation.provinceStateRequired',
                            'Province / State is required.'
                        ) || 'Province / State is required.'
                    );
                }
            }
            return true;
        }),
        zipCode: refine(string(), 'zipCode', (value, context) => {
            if (value?.trim()?.length < 1) {
                return (
                    getTranslation?.(
                        'payment.validation.postalZipCodeRequired',
                        'Postal/Zip Code is required.'
                    ) || 'Postal/Zip Code is required.'
                );
            }
            if (postcodeValidatorExistsForCountry(context.branch[0].country)) {
                // Normalize postal code by removing spaces for validation
                // This allows both "M5V 0P5" and "M5V0P5" formats
                const normalizedValue = value.trim().replace(/\s+/g, '');
                if (!postcodeValidator(normalizedValue, context.branch[0].country)) {
                    return (
                        getTranslation?.(
                            'payment.validation.invalidPostalCode',
                            'Invalid postal code for selected country'
                        ) || 'Invalid postal code for selected country'
                    );
                }
            }

            // if (context.branch[0].country === 'CA' || context.branch[0].country === 'US') {
            //     if (typeof value !== 'string' || value.trim().length === 0) {
            //         return 'Zip Code is required.';
            //     }
            // }
            return true;
        }),
    });
